import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import * as categoriesApi from '../api/catalogCategoriesApi';
import * as importApi from '../api/catalogImportApi';
import * as mediaApi from '../api/catalogMediaApi';
import * as pricingApi from '../api/catalogPricingApi';
import type { ListPricingRulesParams, ResolvePriceParams } from '../api/catalogPricingApi';
import * as productsApi from '../api/catalogProductsApi';
import type {
  AssetResponse,
  BrowseCategoriesParams,
  BrowseProductsParams,
  BulkProductsStockUpdateRequest,
  BulkUpdateResult,
  Category,
  CategoryDetail,
  CategoryListRequest,
  CategoryListResponse,
  CategorySummaryPage,
  CategoryUpsertRequest,
  MediaOwnerType,
  MediaVisibility,
  ExternalProviderConfigRequest,
  ExternalProviderConfigResponse,
  GoogleSheetSettings,
  GoogleSheetSettingsRequest,
  GovernedPricePage,
  GovernedPriceResponse,
  ImportResult,
  ImportRun,
  PriceLifecycleAuditPage,
  PriceLineRequest,
  PriceLineResponse,
  PriceProtectionPolicyRequest,
  PriceProtectionPolicyResponse,
  PricingRulePage,
  PricingRuleRequest,
  PricingRuleResponse,
  PricingRuleUpdateRequest,
  ProductDetail,
  ProductListRequest,
  ProductListResponse,
  ProductPriceUpdateRequest,
  ProductPublication,
  ProductSearchRequest,
  ProductSearchResponse,
  ProductStateUpdateRequest,
  ProductStockUpdateRequest,
  ProductSummaryPage,
  ProductUpsertRequest,
  ResolvedPriceResponse,
  SalesChannelRequest,
  SalesChannelResponse,
  SalesChannelUpdateRequest,
  TenantPricingConfigRequest,
  TenantPricingConfigResponse,
  UploadAssetParams,
} from '../model/types';
import { catalogKeys } from './queryKeys';

type Opts = { enabled?: boolean };
type Err = NormalizedApiError;

// ── Products ────────────────────────────────────────────────────────────────
export function useProducts(
  req: ProductListRequest,
  options?: Opts,
): UseQueryResult<ProductListResponse, Err> {
  return useQuery<ProductListResponse, Err>({
    queryKey: catalogKeys.productsList(req),
    queryFn: () => productsApi.listProducts(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useSearchProducts(
  req: ProductSearchRequest,
  options?: Opts,
): UseQueryResult<ProductSearchResponse, Err> {
  return useQuery<ProductSearchResponse, Err>({
    queryKey: catalogKeys.productsSearch(req),
    queryFn: () => productsApi.searchProducts(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/** Browse the FULL master catalog (drafts included) — epic-catalog-002, story-024. */
export function useBrowseProducts(
  params: BrowseProductsParams,
  options?: Opts,
): UseQueryResult<ProductSummaryPage, Err> {
  return useQuery<ProductSummaryPage, Err>({
    queryKey: catalogKeys.productsBrowse(params),
    queryFn: () => productsApi.browseProducts(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (productId) => productsApi.deleteProduct(productId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.products }),
  });
}

export function useProduct(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductDetail, Err> {
  return useQuery<ProductDetail, Err>({
    queryKey: catalogKeys.product(productId ?? ''),
    queryFn: () => productsApi.getProduct(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { productId: string; body: ProductUpsertRequest }>({
    mutationFn: ({ productId, body }) => productsApi.upsertProduct(productId, body),
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.product(productId) });
    },
  });
}

export function useUpdateProductPrice() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { productId: string; body: ProductPriceUpdateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.updateProductPrice(productId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.products }),
  });
}

export function useUpdateProductStock() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { productId: string; body: ProductStockUpdateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.updateProductStock(productId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.products }),
  });
}

export function useUpdateProductState() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; body: ProductStateUpdateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.updateProductState(productId, body),
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productPublication(productId) });
    },
  });
}

export function useBulkUpdateProductsStock() {
  const qc = useQueryClient();
  return useMutation<BulkUpdateResult, Err, BulkProductsStockUpdateRequest>({
    mutationFn: (body) => productsApi.bulkUpdateProductsStock(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.products }),
  });
}

// ── Products: publication (author → publish to channels) ──────────────────────
export function useProductPublication(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductPublication, Err> {
  return useQuery<ProductPublication, Err>({
    queryKey: catalogKeys.productPublication(productId ?? ''),
    queryFn: () => productsApi.getProductPublication(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

/** Shared cache update after a publication change — the response IS the new publication state. */
function onPublicationChanged(qc: ReturnType<typeof useQueryClient>, data: ProductPublication) {
  qc.setQueryData(catalogKeys.productPublication(data.productId), data);
  void qc.invalidateQueries({ queryKey: catalogKeys.products });
  void qc.invalidateQueries({ queryKey: catalogKeys.product(data.productId) });
}

export function usePublishProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string; channelCode?: string }>({
    mutationFn: ({ productId, channelCode }) => productsApi.publishProduct(productId, channelCode),
    onSuccess: (data) => onPublicationChanged(qc, data),
  });
}

export function useUnpublishProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string; channelCode?: string }>({
    mutationFn: ({ productId, channelCode }) => productsApi.unpublishProduct(productId, channelCode),
    onSuccess: (data) => onPublicationChanged(qc, data),
  });
}

export function useArchiveProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.archiveProduct(productId),
    onSuccess: (data) => onPublicationChanged(qc, data),
  });
}

export function useRestoreProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.restoreProduct(productId),
    onSuccess: (data) => onPublicationChanged(qc, data),
  });
}

// ── Categories ──────────────────────────────────────────────────────────────
export function useCategories(
  req: CategoryListRequest = {},
  options?: Opts,
): UseQueryResult<CategoryListResponse, Err> {
  return useQuery<CategoryListResponse, Err>({
    queryKey: catalogKeys.categoriesList(req),
    queryFn: () => categoriesApi.listCategories(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCategory(
  categoryId: string | undefined,
  options?: Opts,
): UseQueryResult<Category, Err> {
  return useQuery<Category, Err>({
    queryKey: catalogKeys.category(categoryId ?? ''),
    queryFn: () => categoriesApi.getCategory(categoryId as string),
    enabled: (options?.enabled ?? true) && Boolean(categoryId),
  });
}

export function useUpsertCategory() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { categoryId: string; body: CategoryUpsertRequest }>({
    mutationFn: ({ categoryId, body }) => categoriesApi.upsertCategory(categoryId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.categories }),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation<CategoryDetail, Err, CategoryUpsertRequest>({
    mutationFn: (body) => categoriesApi.createCategory(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.categories }),
  });
}

/** Search master-catalog categories — epic-catalog-002, story-024. */
export function useSearchCategories(
  params: BrowseCategoriesParams = {},
  options?: Opts,
): UseQueryResult<CategorySummaryPage, Err> {
  return useQuery<CategorySummaryPage, Err>({
    queryKey: catalogKeys.categoriesSearch(params),
    queryFn: () => categoriesApi.searchCategories(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (categoryId) => categoriesApi.deleteCategory(categoryId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.categories }),
  });
}

// ── Media ─────────────────────────────────────────────────────────────────────
export function useUploadAsset() {
  return useMutation<AssetResponse, Err, UploadAssetParams>({
    mutationFn: (params) => mediaApi.uploadAsset(params),
  });
}

export function useCatalogMediaAssets(
  params: { ownerType: MediaOwnerType; ownerId?: string; visibility?: MediaVisibility },
  options?: Opts,
): UseQueryResult<AssetResponse[], Err> {
  return useQuery<AssetResponse[], Err>({
    queryKey: catalogKeys.mediaAssets(params),
    queryFn: () => mediaApi.listAssets(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteAsset() {
  return useMutation<void, Err, string>({ mutationFn: (assetId) => mediaApi.deleteAsset(assetId) });
}

// ── Import ──────────────────────────────────────────────────────────────────
export function useDownloadTemplate() {
  return useMutation<void, Err, void>({ mutationFn: () => importApi.downloadCatalogTemplate() });
}

export function useImportFile() {
  return useMutation<ImportRun, Err, { file: File; dryRun: boolean }>({
    mutationFn: ({ file, dryRun }) => importApi.importCatalogFile(file, dryRun),
  });
}

export function useGoogleSheetSettings(options?: Opts): UseQueryResult<GoogleSheetSettings, Err> {
  return useQuery<GoogleSheetSettings, Err>({
    queryKey: catalogKeys.googleSheet,
    queryFn: importApi.getGoogleSheetSettings,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpdateGoogleSheetSettings() {
  const qc = useQueryClient();
  return useMutation<GoogleSheetSettings, Err, GoogleSheetSettingsRequest>({
    mutationFn: importApi.updateGoogleSheetSettings,
    onSuccess: (data) => qc.setQueryData(catalogKeys.googleSheet, data),
  });
}

export function useImportFromGoogleSheet() {
  return useMutation<ImportRun, Err, { dryRun: boolean }>({
    mutationFn: ({ dryRun }) => importApi.importFromGoogleSheet(dryRun),
  });
}

// ── Pricing: config ─────────────────────────────────────────────────────────
export function usePricingConfig(options?: Opts): UseQueryResult<TenantPricingConfigResponse, Err> {
  return useQuery<TenantPricingConfigResponse, Err>({
    queryKey: catalogKeys.pricingConfig,
    queryFn: pricingApi.getPricingConfig,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpsertPricingConfig() {
  const qc = useQueryClient();
  return useMutation<TenantPricingConfigResponse, Err, TenantPricingConfigRequest>({
    mutationFn: pricingApi.upsertPricingConfig,
    onSuccess: (data) => qc.setQueryData(catalogKeys.pricingConfig, data),
  });
}

// ── Pricing: rules ────────────────────────────────────────────────────────────
export function usePricingRules(
  params: ListPricingRulesParams,
  options?: Opts,
): UseQueryResult<PricingRulePage, Err> {
  return useQuery<PricingRulePage, Err>({
    queryKey: catalogKeys.pricingRules(params),
    queryFn: () => pricingApi.listPricingRules(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreatePricingRule() {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, Err, PricingRuleRequest>({
    mutationFn: pricingApi.createPricingRule,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useUpdatePricingRule() {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, Err, { ruleId: string; body: PricingRuleUpdateRequest }>({
    mutationFn: ({ ruleId, body }) => pricingApi.updatePricingRule(ruleId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useDeactivatePricingRule() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (ruleId) => pricingApi.deactivatePricingRule(ruleId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

// ── Pricing: provider configs ─────────────────────────────────────────────────
export function useProviderConfigs(
  options?: Opts,
): UseQueryResult<ExternalProviderConfigResponse[], Err> {
  return useQuery<ExternalProviderConfigResponse[], Err>({
    queryKey: catalogKeys.providerConfigs,
    queryFn: pricingApi.listProviderConfigs,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateProviderConfig() {
  const qc = useQueryClient();
  return useMutation<ExternalProviderConfigResponse, Err, ExternalProviderConfigRequest>({
    mutationFn: pricingApi.createProviderConfig,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.providerConfigs }),
  });
}

export function useDeactivateProviderConfig() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (configId) => pricingApi.deactivateProviderConfig(configId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.providerConfigs }),
  });
}

// ── Pricing: approvals ────────────────────────────────────────────────────────
export function usePriceApprovals(
  params: { page?: number; size?: number },
  options?: Opts,
): UseQueryResult<GovernedPricePage, Err> {
  return useQuery<GovernedPricePage, Err>({
    queryKey: catalogKeys.approvals(params),
    queryFn: () => pricingApi.listPriceApprovals(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useApprovePrice() {
  const qc = useQueryClient();
  return useMutation<GovernedPriceResponse, Err, string>({
    mutationFn: (priceId) => pricingApi.approvePrice(priceId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useRejectPrice() {
  const qc = useQueryClient();
  return useMutation<GovernedPriceResponse, Err, { priceId: string; reason?: string }>({
    mutationFn: ({ priceId, reason }) => pricingApi.rejectPrice(priceId, { reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

// ── Pricing: protection policy + audit ─────────────────────────────────────────
export function usePriceProtectionPolicy(
  options?: Opts,
): UseQueryResult<PriceProtectionPolicyResponse, Err> {
  return useQuery<PriceProtectionPolicyResponse, Err>({
    queryKey: catalogKeys.protectionPolicy,
    queryFn: pricingApi.getPriceProtectionPolicy,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpsertPriceProtectionPolicy() {
  const qc = useQueryClient();
  return useMutation<PriceProtectionPolicyResponse, Err, PriceProtectionPolicyRequest>({
    mutationFn: pricingApi.upsertPriceProtectionPolicy,
    onSuccess: (data) => qc.setQueryData(catalogKeys.protectionPolicy, data),
  });
}

export function usePriceLifecycleAudit(
  params: { productId?: string; page?: number; size?: number },
  options?: Opts,
): UseQueryResult<PriceLifecycleAuditPage, Err> {
  return useQuery<PriceLifecycleAuditPage, Err>({
    queryKey: catalogKeys.audit(params),
    queryFn: () => pricingApi.getPriceLifecycleAudit(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Pricing: sales channels (Phase 2b/3) ─────────────────────────────────────
export function useSalesChannels(options?: Opts): UseQueryResult<SalesChannelResponse[], Err> {
  return useQuery<SalesChannelResponse[], Err>({
    queryKey: catalogKeys.salesChannels,
    queryFn: pricingApi.listSalesChannels,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateSalesChannel() {
  const qc = useQueryClient();
  return useMutation<SalesChannelResponse, Err, SalesChannelRequest>({
    mutationFn: pricingApi.createSalesChannel,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.salesChannels }),
  });
}

export function useUpdateSalesChannel() {
  const qc = useQueryClient();
  return useMutation<SalesChannelResponse, Err, { code: string; body: SalesChannelUpdateRequest }>({
    mutationFn: ({ code, body }) => pricingApi.updateSalesChannel(code, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.salesChannels }),
  });
}

// ── Pricing: price lines (Phase 2b/3) ─────────────────────────────────────────
export function useProductPriceLines(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<PriceLineResponse[], Err> {
  return useQuery<PriceLineResponse[], Err>({
    queryKey: catalogKeys.priceLines(productId ?? ''),
    queryFn: () => pricingApi.listPriceLines(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

export function useSubmitPriceLine() {
  const qc = useQueryClient();
  return useMutation<PriceLineResponse, Err, PriceLineRequest>({
    mutationFn: pricingApi.submitPriceLine,
    onSuccess: (line) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.priceLines(line.productId) });
      void qc.invalidateQueries({ queryKey: catalogKeys.approvals({}) });
    },
  });
}

// ── Pricing: price resolution (Phase 2b/3) ───────────────────────────────────
export function usePriceResolution(
  params: ResolvePriceParams | undefined,
  options?: Opts,
): UseQueryResult<ResolvedPriceResponse, Err> {
  return useQuery<ResolvedPriceResponse, Err>({
    queryKey: catalogKeys.priceResolution(params ?? { productId: '' }),
    queryFn: () => pricingApi.resolvePrice(params as ResolvePriceParams),
    enabled: (options?.enabled ?? true) && Boolean(params?.productId),
  });
}
